// @flow
import { Formik } from 'formik'
import usePermissions from 'hooks/usePermissions'
import { useLoadCustomerFields } from 'queries/customerFields/useLoadCustomerField'
import { useLoadCustomer } from 'queries/customers/useLoadCustomer'
import { useUpdateCustomers } from 'queries/customers/useUpdateCustomer'
import * as React from 'react'
import { useParams } from 'react-router-dom'
import type { PartialUpdateCustomer } from 'types/customer'
import Ui from 'ui'
import Button from 'ui/atoms/Button'
import Icons from 'ui/Icons'
import * as Yup from 'yup'

import Loading from '@/components/Loading'

import ClientDemographicsTableContent from './ClientDemographicsTableContent'
import ClientDemographicsTableCustomFields from './ClientDemographicsTableCustomFields'
import ClientDemographicsTableDemographics from './ClientDemographicsTableDemographics'
import ClientDemographicsTableEmergencyContact from './ClientDemographicsTableEmergencyContact'
function getDifferences(obj1: ?{ [string]: any }, obj2: ?{ [string]: any }) {
  let diff: { [string]: any } = {}

  if (!obj1 && obj2) return obj2
  if (!obj2 && obj1) return obj1
  if (!obj1 && !obj2) return diff

  const allKeys = new Set([
    ...Object.keys(obj1 || {}),
    ...Object.keys(obj2 || {}),
  ])

  for (let key of allKeys) {
    if ((obj1 || {})[key] !== (obj2 || {})[key]) {
      diff[key] = obj2?.[key]
    }
  }

  return diff
}

const schema = Yup.object().shape({
  first_name: Yup.string().required('First name is required'),
  last_name: Yup.string().required('Last name is required'),
  date_birth: Yup.date()
    .required('Date of birth is required')
    .typeError('Date of birth is required'),
  emergency_contact: Yup.object()
    .shape({
      first_name: Yup.string().required('First name is required'),
      last_name: Yup.string().required('Last name is required'),
    })
    .nullable(),
  email: Yup.string().email('Invalid email').nullable(),
})

function ClientDemographicsTable(): React.Node {
  const { clientId } = useParams()
  const { data: customerFields, isLoading: isCustomerFieldsLoading } =
    useLoadCustomerFields()
  const { data: clientInDetail, isLoading: isCustomerLoading } =
    useLoadCustomer(clientId)
  const { canEditClientProfile } = usePermissions()
  const { updateCustomer, isLoading: isUpdateCustomerLoading } =
    useUpdateCustomers()

  const [isUpdating, setUpdating] = React.useState(false)
  const [isEditMode, setEditMode] = React.useState(false)

  const onUpdateProfileData = React.useCallback(
    async (values) => {
      if (!clientInDetail) return

      const data: PartialUpdateCustomer = getDifferences(clientInDetail, values)

      data.emergency_contact = getDifferences(
        clientInDetail.emergency_contact,
        values.emergency_contact
      )
      if (!Object.keys(data.emergency_contact || {}).length) {
        delete data.emergency_contact
      }

      data.custom_fields = getDifferences(
        clientInDetail.custom_fields,
        values.custom_fields
      )
      if (!Object.keys(data.custom_fields || {}).length) {
        delete data.custom_fields
      }

      // $FlowFixMe
      delete data.responses
      // $FlowFixMe
      delete data.packet_responses
      // $FlowFixMe
      delete data.insurances

      setUpdating(true)
      try {
        await updateCustomer({ id: clientId, data })
        setEditMode(false)
      } catch (error) {
      } finally {
        setUpdating(false)
      }
    },
    [clientId, clientInDetail, updateCustomer]
  )

  const cancel = () => setEditMode(false)

  if (isCustomerLoading || isCustomerFieldsLoading) {
    return <Loading className="!h-60" />
  }
  return (
    <div className="mb-10 rounded-bl rounded-br rounded-tr border bg-white pb-3">
      <Formik
        initialValues={clientInDetail}
        onSubmit={onUpdateProfileData}
        validationSchema={schema}
      >
        {({ handleSubmit, errors }) => {
          return (
            <>
              <div className="flex justify-end align-middle">
                {isEditMode && (
                  <div className="flex">
                    <Ui.ButtonClear
                      className="cancel-btn text-red-500"
                      onClick={cancel}
                    >
                      Cancel
                    </Ui.ButtonClear>
                    <Button
                      $primary
                      loading={
                        isUpdating ||
                        isUpdateCustomerLoading ||
                        isCustomerLoading ||
                        isCustomerFieldsLoading
                      }
                      onClick={handleSubmit}
                      type="submit"
                    >
                      Save
                    </Button>
                  </div>
                )}
                {!isEditMode && canEditClientProfile && (
                  <Ui.ButtonClear
                    className="text-blue-700"
                    onClick={() => setEditMode(true)}
                  >
                    <Icons.Pencil
                      className="h-6"
                      color="mr-2.5 text-blue-700"
                    />
                    Edit
                  </Ui.ButtonClear>
                )}
              </div>

              <ClientDemographicsTableContent title="Demographics">
                <ClientDemographicsTableDemographics isEditMode={isEditMode} />
              </ClientDemographicsTableContent>
              <ClientDemographicsTableContent title="Emergency Contact">
                <ClientDemographicsTableEmergencyContact
                  isEditMode={isEditMode}
                />
              </ClientDemographicsTableContent>
              {customerFields?.length ? (
                <ClientDemographicsTableContent title="Custom Fields">
                  <ClientDemographicsTableCustomFields
                    isEditMode={isEditMode}
                  />
                </ClientDemographicsTableContent>
              ) : null}
            </>
          )
        }}
      </Formik>
      <div />
    </div>
  )
}

export default ClientDemographicsTable
